import { useCallback, useEffect, useMemo, useState } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import { useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { queryClient } from 'src/query-client';
import { MediaViewerOutletContextState } from '@layouts/types';
import { getProxyClient } from '@api/client';

// Fraction of the server's grant TTL after which we proactively
// re-mint, so a user who keeps the review tab open for hours doesn't
// hit a sudden 403 mid-browsing. The server config is the source of
// truth for absolute expiry; this just controls the renewal cadence.
const RENEW_AT_FRACTION = 0.8;

type GrantState =
  | { ready: false; error: null }
  | { ready: true; error: null }
  | { ready: false; error: 'forbidden' | 'unknown' };

/**
 * Mints a server-side grant cookie that authorises the proxy iframe
 * to render this user's review session, and re-mints it before
 * expiry. The hook returns `ready=false` until the first mint
 * succeeds; render the iframe behind a `ready` gate so its first
 * request already carries the cookie.
 *
 * Failures bubble up via `error`. The caller decides UX (retry
 * button, error banner, etc.).
 */
export function useProxyGrant(
  assetVersionId: string,
  proxyUrl: string
): GrantState {
  const proxyOrigin = useMemo(() => new URL(proxyUrl).origin, [proxyUrl]);
  const proxyClient = useMemo(
    () => getProxyClient(new URL(proxyOrigin)),
    [proxyOrigin]
  );
  const { mintWebsiteProxyGrant } =
    useOutletContext<MediaViewerOutletContextState>();

  const [cookieConsent, setCookieConsent] = useState<boolean>(
    CookieConsent.acceptedCategory('necessary')
  );
  useEffect(() => {
    if (cookieConsent) return;
    const interval = setInterval(() => {
      if (CookieConsent.acceptedCategory('necessary')) {
        setCookieConsent(true);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [cookieConsent]);

  const query = useQuery({
    enabled: cookieConsent,
    queryKey: ['proxy', 'grant', proxyOrigin, assetVersionId],
    staleTime: Infinity,
    queryFn: async () => {
      const result =
        await proxyClient.websiteProxy.websiteProxyControllerGrantAccess(
          mintWebsiteProxyGrant(assetVersionId)
        );
      return result.data;
    }
  });

  const _mapQueryToState = useCallback((): GrantState => {
    if (query.isLoading) {
      return { ready: false, error: null };
    }
    if (query.isError) {
      return {
        ready: false,
        error:
          isAxiosError(query.error) && query.error.response?.status === 403
            ? 'forbidden'
            : 'unknown'
      };
    }
    return {
      ready: true,
      error: null
    };
  }, [query.isLoading, query.isError, query.error]);

  const [state, setState] = useState<GrantState>(() => _mapQueryToState());

  useEffect(() => {
    setState(_mapQueryToState);
  }, [_mapQueryToState]);

  useEffect(() => {
    if (!query.data?.expiresAt) return;
    const expiresAt = new Date(query.data.expiresAt).getTime();
    const now = Date.now();
    const ttl = expiresAt - now;
    const renewIn = ttl * RENEW_AT_FRACTION;
    const timeoutId = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: ['proxy', 'grant', proxyOrigin]
      });
    }, renewIn);
    return () => clearTimeout(timeoutId);
  }, [query.data?.expiresAt, proxyOrigin]);

  return state;
}
